import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./dashboard.css";
import "../common/Util.css";
import Loader from "../common/Loader.jsx";
import { getNextActionOverviewApi } from "./dashboard-api.js";

// Urgency reads as colour before it reads as text, so the same three shades
// carry it everywhere on this page.
const PRIORITY_STYLE = {
  high: { bg: "#FFEBE6", text: "#BF2600", label: "Now" },
  medium: { bg: "#FFF7E6", text: "#974F0C", label: "Soon" },
  low: { bg: "#F1F2F4", text: "#44546F", label: "When free" },
};

export const NextActionDashboard = () => {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState("all");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await getNextActionOverviewApi();
        if (!live) return;
        if (response?.success === true) {
          setData(response.data);
        } else {
          setError(response?.message || "Could not load the action list");
        }
      } catch (err) {
        if (live) setError("Could not load the action list");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const modules = data?.modules ?? [];
  const shown = active === "all" ? modules : modules.filter((m) => m.key === active);

  // Most view pages read the record id off router state; quotation takes it
  // in the path. A trailing slash on the route is the backend saying which.
  const openRecord = (module, item) =>
    module.route.endsWith("/")
      ? navigate(`${module.route}${item.id}`)
      : navigate(module.route, { state: { id: item.id } });

  return (
    <div className="w-100 p-3 h-100" style={{ backgroundColor: "#F7F8FC" }}>
      <div className="d-flex justify-content-between w-100">
        <div className="header-tabs">
          <button onClick={() => navigate("/dashboard")} className="dashboard-tab-btn">Leads Hub</button>
          <button onClick={() => navigate("/revenue")} className="dashboard-tab-btn">Revenue Overview</button>
          <button onClick={() => navigate("/pipeline")} className="dashboard-tab-btn">Pipeline</button>
          <button onClick={() => navigate("/forecast")} className="dashboard-tab-btn">Forecast</button>
          <button onClick={() => navigate("/next-action")} className="dashboard-tab-btn active">Next Actions</button>
        </div>
      </div>

      {loading ? (
        <Loader height="400px" />
      ) : error ? (
        <p className="text-danger fs-14 fw-400 text-center py-5 m-0">{error}</p>
      ) : (
        <>
          <div className="row mt-3">
            <div className="col-xl col-md-4 col-6 mb-3 mb-xl-0">
              <button
                className={`bg-white rounded-4 p-3 d-flex flex-column gap-1 h-100 w-100 text-start ${
                  active === "all" ? "border border-2" : "border-0"
                }`}
                style={{ minHeight: "110px", borderColor: active === "all" ? "#6929E2" : undefined }}
                onClick={() => setActive("all")}
              >
                <p className="textLightGrey fs-13 fw-400 m-0">Everything waiting</p>
                <p className="fs-20 fw-600 textDarkBlue1 m-0">{data?.total ?? 0}</p>
                <p className="fs-12 fw-400 m-0" style={{ color: PRIORITY_STYLE.high.text }}>
                  {data?.high_priority ?? 0} need attention now
                </p>
              </button>
            </div>
            {modules.map((module) => (
              <div key={module.key} className="col-xl col-md-4 col-6 mb-3 mb-xl-0">
                <button
                  className={`bg-white rounded-4 p-3 d-flex flex-column gap-1 h-100 w-100 text-start ${
                    active === module.key ? "border border-2" : "border-0"
                  }`}
                  style={{
                    minHeight: "110px",
                    borderColor: active === module.key ? "#6929E2" : undefined,
                  }}
                  onClick={() => setActive(module.key)}
                >
                  <p className="textLightGrey fs-13 fw-400 m-0">{module.label}</p>
                  <p className="fs-20 fw-600 textDarkBlue1 m-0">{module.total}</p>
                  <p className="textLightGrey fs-12 fw-400 m-0">
                    {module.total > module.items.length
                      ? `showing top ${module.items.length}`
                      : "all shown"}
                  </p>
                </button>
              </div>
            ))}
          </div>

          {shown.map((module) => (
            <div key={module.key} className="row mt-3">
              <div className="col-12">
                <div className="bg-white rounded-4 p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-semibold m-0">{module.label}</h5>
                    <span className="textLightGrey fs-12 fw-400">
                      {module.total} item(s) need a decision
                    </span>
                  </div>

                  {module.items.length === 0 ? (
                    <p className="textLightGrey fs-14 fw-400 m-0 py-3">
                      Nothing outstanding here.
                    </p>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {module.items.map((item) => {
                        const tone = PRIORITY_STYLE[item.priority] ?? PRIORITY_STYLE.low;
                        return (
                          <button
                            key={`${module.key}-${item.id}`}
                            className="border-0 rounded-3 p-3 text-start w-100 d-flex gap-3 align-items-start"
                            style={{ backgroundColor: "#F7F8FC" }}
                            onClick={() => openRecord(module, item)}
                          >
                            <span
                              className="rounded-pill px-2 py-1 fs-12 fw-600 flex-shrink-0"
                              style={{ backgroundColor: tone.bg, color: tone.text }}
                            >
                              {tone.label}
                            </span>
                            <span className="flex-grow-1">
                              <span className="d-block fs-14 fw-600 textDarkBlue1">
                                {item.action}
                              </span>
                              <span className="d-block fs-13 fw-400 textLightGrey">
                                {item.code ? `${item.code} · ` : ""}
                                {item.title || "Untitled"}
                                {item.subtitle ? ` · ${item.subtitle}` : ""}
                              </span>
                              <span className="d-block fs-12 fw-400 textLightGrey">
                                {item.reason}
                              </span>
                              {/* The record's own stored suggestion, when the
                                  module page has already generated one. */}
                              {item.suggestion && (
                                <span className="d-block fs-12 fw-400 mt-1 textPurple">
                                  Suggested: {item.suggestion}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default NextActionDashboard;
